import React, { useState, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Box, CircularProgress } from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useProject } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { authDownload, authFetch } from '../utils/authFetch';
import { sanitizeFileNameSegment } from '../utils/stringSanitization';
import { calculatePixelsPerFrame } from '../components/VideoSettings';
import { formatFramesToClock } from '../utils/cardTiming';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './Renders.css';

interface RenderItem {
  version: number;
  status: string;
  created_at: string;
  file_name: string;
  video_settings: {
    background_color?: string;
    fps_num?: number;
    resolution?: string;
    transparent?: boolean;
    video_time?: number;
    video_time_frames?: number;
    aspect_ratio?: string;
    video_format?: string;
  };
}

// Mock API function for list_renders
// const mockListRenders = async (projectId: string): Promise<RenderItem[]> => {
//   // Simulate API delay
//   await new Promise(resolve => setTimeout(resolve, 300));
  
//   return [
//     {
//       version: 29,
//       status: 100,
//       start_time: '2025-10-11 10:30:00',
//       end_time: '2025-10-11 10:35:00',
//       background_color: '#000000',
//       fps_num: 30,
//       video_width: 1920,
//       transparent: false,
//       video_time: 120,
//       aspect_ratio: '16:9'
//     },
//     {
//       version: 28,
//       status: 65,
//       start_time: '2025-10-11 09:15:00',
//       end_time: null,
//       background_color: '#FFFFFF',
//       fps_num: 24,
//       video_width: 3840,
//       transparent: false,
//       video_time: 180,
//       aspect_ratio: '16:9'
//     },
//     {
//       version: 27,
//       status: -1,
//       start_time: '2025-10-11 08:00:00',
//       end_time: '2025-10-11 08:05:00',
//       background_color: '#000000',
//       fps_num: 30,
//       video_width: 1920,
//       transparent: true,
//       video_time: 150,
//       aspect_ratio: '21:9'
//     },
//     {
//       version: 26,
//       status: 100,
//       start_time: '2025-10-10 16:45:00',
//       end_time: '2025-10-10 16:50:00',
//       background_color: '#1a1a1a',
//       fps_num: 60,
//       video_width: 1920,
//       transparent: false,
//       video_time: 90,
//       aspect_ratio: '16:9'
//     },
//     {
//       version: 25,
//       status: 33,
//       start_time: '2025-10-10 14:20:00',
//       end_time: null,
//       background_color: '#000000',
//       fps_num: 30,
//       video_width: 2560,
//       transparent: false,
//       video_time: 200,
//       aspect_ratio: '16:9'
//     }
//   ];
// };

const realListRenders = async (projectId: string): Promise<RenderItem[]> => {
  const response = await authFetch(`${getApiUrl()}/list-renders?projectId=${projectId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch renders');
  }
  const data = await response.json();
  return data.renders || [];
};

// Active API function - switch between mock and real
// const listRenders = mockListRenders;
const listRenders = realListRenders;

// ============================================================================

// Mock API function for get_render
// const mockGetRender = async (projectId: string, version: number, fileName: string): Promise<void> => {
//   // Simulate API delay
//   await new Promise(resolve => setTimeout(resolve, 500));
  
//   // Create a mock video file download
//   const blob = new Blob(['Mock video content'], { type: 'video/mp4' });
//   const url = window.URL.createObjectURL(blob);
//   const link = document.createElement('a');
//   link.href = url;
//   link.download = `render_v${version}_${new Date().toISOString().split('T')[0]}.mp4`;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
//   window.URL.revokeObjectURL(url);
// };

const realGetRender = async (
  projectId: string,
  version: number,
  fileName: string,
  projectName: string,
  videoFormat?: string,
  resolution?: string,
  aspectRatio?: string
): Promise<void> => {
  const extension: string = fileName.includes('.') ? fileName.split('.').pop() || 'mp4' : 'mp4';

  const safeProjectNameSegment: string = sanitizeFileNameSegment(projectName, { fallback: 'project' });
  const safeVideoFormatSegment: string = sanitizeFileNameSegment(videoFormat, { fallback: extension });
  const safeResolutionSegment: string = sanitizeFileNameSegment(resolution, { fallback: 'res' });
  const safeAspectRatioSegment: string = sanitizeFileNameSegment(aspectRatio, { fallback: 'aspect' });
  const isoDate: string = new Date().toISOString();
  const dateSegment: string = isoDate.split('T')[0];
  const downloadFileName: string = `${safeProjectNameSegment}_${safeVideoFormatSegment}_${safeResolutionSegment}_${safeAspectRatioSegment}_v${version}_${dateSegment}.${extension}`;
  const downloadUrl: string = `${getApiUrl()}/get-render?projectId=${encodeURIComponent(
    projectId
  )}&version=${encodeURIComponent(String(version))}&downloadName=${encodeURIComponent(downloadFileName)}`;

  await authDownload(downloadUrl, downloadFileName);
};

// Active API function - switch between mock and real
// const getRender = mockGetRender;
const getRender = realGetRender;

// ============================================================================

// Mock API function for delete_render
// const mockDeleteRender = async (projectId: string, version: number): Promise<{ success: boolean }> => {
// Simulate API delay
// await new Promise(resolve => setTimeout(resolve, 300));
  
// return { success: true };
// };

 const realDeleteRender = async (projectId: string, version: number): Promise<{ success: boolean }> => {
  const response = await authFetch(`${getApiUrl()}/delete-render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, version })
  });
  if (!response.ok) throw new Error('Failed to delete render');
  return response.json();
};

// Active API function - switch between mock and real
const deleteRender = realDeleteRender;

// ============================================================================

const RENDERS_REFRESH_INTERVAL_MS = 10000;

const Renders: React.FC = () => {
  const { selectedProject } = useProject();
  const navigate = useNavigate();
  const [renders, setRenders] = useState<RenderItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [countdownNow, setCountdownNow] = useState<number>(() => Date.now());
  const [nextRefreshAt, setNextRefreshAt] = useState<number>(() => Date.now() + RENDERS_REFRESH_INTERVAL_MS);

  const fetchRenders = useCallback(async () => {
    if (!selectedProject) {
      setError('No project selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await listRenders(selectedProject.id);
      setRenders(data);
    } catch (err) {
      console.error('Error fetching renders:', err);
      setError('Failed to load renders');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    const refreshRenders = () => {
      setNextRefreshAt(Date.now() + RENDERS_REFRESH_INTERVAL_MS);
      fetchRenders();
    };

    refreshRenders();
    
    // Auto-refresh every 10 seconds to show progress updates
    const interval = setInterval(refreshRenders, RENDERS_REFRESH_INTERVAL_MS);
    const countdownInterval = setInterval(() => setCountdownNow(Date.now()), 250);
    
    return () => {
      clearInterval(interval);
      clearInterval(countdownInterval);
    };
  }, [selectedProject, fetchRenders]);

  const refreshRemainingMs: number = Math.max(0, nextRefreshAt - countdownNow);
  const refreshRemainingSeconds: number = Math.ceil(refreshRemainingMs / 1000);
  const refreshElapsedPercent: number = Math.min(
    100,
    Math.max(0, ((RENDERS_REFRESH_INTERVAL_MS - refreshRemainingMs) / RENDERS_REFRESH_INTERVAL_MS) * 100)
  );
  const isRefreshingExistingRows: boolean = loading && renders.length > 0;

  const handleDownload = async (renderItem: RenderItem) => {
    if (!selectedProject) return;
    const { version, file_name: fileName, video_settings: videoSettings } = renderItem;
    
    try {
      await getRender(
        selectedProject.id,
        version,
        fileName,
        selectedProject.name,
        videoSettings?.resolution,
        videoSettings?.video_format,
        videoSettings?.aspect_ratio
      );
    } catch (err) {
      console.error('Error downloading render:', err);
      alert('Failed to download render');
    }
  };

  const handleDelete = async (version: number) => {
    if (!selectedProject) return;
    
    if (!window.confirm(`Are you sure you want to delete render version ${version}?`)) {
      return;
    }
    
    try {
      await deleteRender(selectedProject.id, version);
      // Refresh the list after deletion
      await fetchRenders();
    } catch (err) {
      console.error('Error deleting render:', err);
      alert('Failed to delete render');
    }
  };

  const formatDateTime = (dateString: string): string => {
    // Parse the UTC date string and convert to user's local timezone
    const utcDate = new Date(dateString + 'Z'); // Add 'Z' to explicitly mark as UTC
    const localDate = toZonedTime(utcDate, Intl.DateTimeFormat().resolvedOptions().timeZone);
    return format(localDate, 'dd/MM/yyyy - HH:mm:ss');
  };

  const StatusCellRenderer = (props: any) => {
    const status: string = props.value;
    const statusNum: number = parseInt(status, 10);
    
    if (statusNum === -1) {
      return <span style={{ color: 'red', fontWeight: 'bold' }}>Failed</span>;
    } else if (statusNum === 100) {
      return <span style={{ color: 'green', fontWeight: 'bold' }}>Complete</span>;
    } else if (statusNum === 0) {
      return <span style={{ color: 'orange', fontWeight: 'bold' }}>Pending</span>;
    } else {
      return <span style={{ color: 'orange', fontWeight: 'bold' }}>{statusNum}%</span>;
    }
  };

  const getResolutionLabel = (videoSettings?: RenderItem['video_settings']): string => {
    if (!videoSettings) {
      return '';
    }
    const explicitResolution: string | undefined = videoSettings.resolution;
    if (explicitResolution && explicitResolution.trim()) {
      return explicitResolution;
    }
    const widthRaw: unknown = (videoSettings as Record<string, unknown>).video_width ?? (videoSettings as Record<string, unknown>).width;
    const width: number = typeof widthRaw === 'number' ? widthRaw : Number(widthRaw);
    if (Number.isFinite(width)) {
      if (width >= 4000) return '4K';
      if (width >= 3800) return 'UHD';
      if (width >= 2000) return '2K';
      if (width >= 1200) return 'HD';
      return `${Math.round(width)}px`;
    }
    return '';
  };

  const ActionsCellRenderer = (props: { data: RenderItem }) => {
    const renderItem: RenderItem = props.data;
    const { version, status } = renderItem;
    const statusNum: number = parseInt(status, 10);
    const isComplete: boolean = statusNum === 100;
    
    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={() => handleDownload(renderItem)}
          disabled={!isComplete}
          style={{
            padding: '4px 12px',
            cursor: isComplete ? 'pointer' : 'not-allowed',
            opacity: isComplete ? 1 : 0.5,
            backgroundColor: isComplete ? '#319204' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          Download
        </button>
        <button
          onClick={() => handleDelete(version)}
          style={{
            padding: '4px 12px',
            cursor: 'pointer',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          Delete
        </button>
      </div>
    );
  };

  const columnDefs: ColDef[] = [
    {
      field: 'version',
      headerName: 'Version',
      filter: true,
      width: 80
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      filter: true, 
      cellRenderer: StatusCellRenderer,
      width: 110,
      maxWidth: 130
    },
    {
      field: 'video_settings.video_format',
      headerName: 'Video Format',
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.video_format,
      width: 130,
      maxWidth: 150
    },
    { 
      field: 'video_settings.fps_num', 
      headerName: 'FPS', 
      filter: true, 
      valueGetter: (params: any) => params.data?.video_settings?.fps_num,
      width: 80,
      maxWidth: 95
    },
    { 
      field: 'video_settings.resolution', 
      headerName: 'Resolution', 
      filter: true, 
      valueGetter: (params: any) => getResolutionLabel(params.data?.video_settings),
      width: 115,
      maxWidth: 135
    },
    { 
      field: 'video_settings.video_time_frames', 
      headerName: 'Duration',
      filter: true, 
      valueGetter: (params: any) => {
        const vs = params.data?.video_settings;
        const durationFrames: number = Number(vs?.video_time_frames);
        if (Number.isFinite(durationFrames)) return durationFrames;
        const videoTime: number = Number(vs?.video_time);
        const framesPerSecond: number = Number(vs?.fps_num);
        return Number.isFinite(videoTime) && Number.isFinite(framesPerSecond)
          ? Math.round(videoTime * framesPerSecond)
          : null;
      },
      valueFormatter: (params: any) => {
        const durationFrames: number = Number(params.value);
        const framesPerSecond: number = Number(params.data?.video_settings?.fps_num);
        if (
          !Number.isFinite(durationFrames) ||
          !Number.isFinite(framesPerSecond) ||
          durationFrames < 0 ||
          framesPerSecond <= 0
        ) {
          return '';
        }
        return formatFramesToClock(Math.round(durationFrames), framesPerSecond);
      },
      width: 140,
      maxWidth: 160
    },
    {
      headerName: 'Px/Frame',
      filter: true,
      valueGetter: (params: any) => {
        const vs = params.data?.video_settings;
        if (!vs?.resolution || !vs?.aspect_ratio) return null;
        return calculatePixelsPerFrame({
          resolution: vs.resolution,
          aspect_ratio: vs.aspect_ratio,
          fps_num: vs.fps_num,
          video_time: vs.video_time,
          video_time_frames: vs.video_time_frames,
        });
      },
      width: 100,
      maxWidth: 115
    },
    { 
      field: 'video_settings.aspect_ratio', 
      headerName: 'Aspect Ratio', 
      filter: true, 
      valueGetter: (params: any) => params.data?.video_settings?.aspect_ratio,
      width: 125,
      maxWidth: 145
    },
    {
      field: 'video_settings.background_color',
      headerName: 'BG Color',
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.background_color,
      width: 110,
      maxWidth: 130
    },
    {
      field: 'video_settings.transparent',
      headerName: 'Transparent',
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.transparent,
      width: 115,
      maxWidth: 135
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      filter: true,
      valueFormatter: (params: any) => params.value ? formatDateTime(params.value) : '',
      width: 180
    },
    {
      field: 'actions',
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      filter: false,
      width: 190,
      minWidth: 190,
      pinned: 'right'
    }
  ];

  // Mandatory: selectedProject must exist to render this component
  if (!selectedProject) {
    return null;
  }

  return (
    <div className="renders-page">
      <button 
        onClick={() => navigate('/app')}
        className="fancy-button"
        style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}
      >
        <ArrowBackIcon style={{ fontSize: '18px' }} />
        Back to Editor
      </button>
      <div className="renders-title-row">
        <h1>Renders - {selectedProject.name}</h1>
        <Box
          className="renders-refresh-ring"
          aria-label={isRefreshingExistingRows ? 'Updating renders' : `Next renders update in ${refreshRemainingSeconds} seconds`}
        >
          <CircularProgress
            variant="determinate"
            value={isRefreshingExistingRows ? 100 : refreshElapsedPercent}
            size={38}
            thickness={4}
          />
          <span>{isRefreshingExistingRows ? '...' : `${refreshRemainingSeconds}s`}</span>
        </Box>
      </div>
      
      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
          {error}
        </div>
      )}
      {loading && renders.length === 0 ? (
        <p>Loading renders...</p>
      ) : (
        <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
          <AgGridReact
            rowData={renders}
            columnDefs={columnDefs}
            defaultColDef={{
              resizable: true,
              sortable: false,
              filter: true
            }}
            animateRows={true}
            rowSelection="single"
          />
        </div>
      )}
    </div>
  );
};

export default Renders;
